import React from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import type { WoundResponse } from '../../services/api';
import WoundTableRow from './WoundTableRow';
import { useIsMobile } from '../../hooks/useIsMobile';

interface WoundsTableProps {
  wounds: WoundResponse[];
  canEdit?: boolean;
  canDiscontinue?: boolean;
  onEdit: (wound: WoundResponse) => void;
  onDelete: (wound: WoundResponse) => void;
  onDiscontinue: (
    woundId: string,
    date: Date,
    reason: string,
    comments: string
  ) => Promise<void>;
  onOpenAssessments: (wound: WoundResponse) => void;
}

const WoundsTable: React.FC<WoundsTableProps> = ({
  wounds,
  canEdit = true,
  canDiscontinue = false,
  onEdit,
  onDelete,
  onDiscontinue,
  onOpenAssessments,
}) => {
  const isMobile = useIsMobile();
  // Active wounds first, then healed — within each group, most recently created first
  const sorted = [...wounds].sort((a, b) => {
    const aHealed = !!a.discontinued_date;
    const bHealed = !!b.discontinued_date;
    if (aHealed !== bHealed) return aHealed ? 1 : -1;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  // Per-wound props shared by the desktop row and the mobile card.
  const rowProps = (wound: WoundResponse) => ({
    wound,
    assessmentCount: wound.assessment_count ?? 0,
    canEdit,
    canDiscontinue,
    onEdit,
    onDelete,
    onDiscontinue,
    onOpenAssessments,
  });

  // Below Tailwind's `md` breakpoint the fixed-width table overflows, so render
  // stacked cards instead of a horizontally scrolling table.
  if (isMobile) {
    return (
      <div className='space-y-3'>
        {sorted.map(wound => (
          <WoundTableRow key={wound.id} variant='card' {...rowProps(wound)} />
        ))}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className='w-[120px]'>Status</TableHead>
          <TableHead>Wound</TableHead>
          <TableHead className='w-[150px]'>Onset</TableHead>
          <TableHead className='w-[240px]'>Assessments</TableHead>
          <TableHead className='w-[260px]'>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map(wound => (
          <WoundTableRow key={wound.id} {...rowProps(wound)} />
        ))}
      </TableBody>
    </Table>
  );
};

export default WoundsTable;
